export type LongwallsJarvisBridgeOverview = {
  generatedAt: string;
  connection: {
    status: "connected" | "degraded" | "offline";
    summary: string;
    apiBaseUrl: string;
    uiBaseUrl: string | null;
    internalKeyConfigured: boolean;
    reachableSlices: string[];
    failedSlices: Array<{ id: string; reason: string }>;
  };
  readiness: {
    overallScore: number | null;
    releaseDecision: string | null;
    truthStatement: string | null;
    nextLeverage: string | null;
    greenGates: number;
    watchGates: number;
    riskGates: number;
  };
  founder: {
    pendingApprovals: number;
    openIncidents: number;
    leverageScore: number | null;
    criticalSignals: string[];
    overnightHeadline: string | null;
  };
  tactical: {
    programsTracked: number;
    totalOpenTasks: number;
    blockedTasks: number;
    founderPendingTasks: number;
    recentOperations: number;
    defaultLane: string | null;
  };
  operator: {
    totalAdapters: number;
    healthyAdapters: number;
    degradedAdapters: number;
    activeIncidents: number;
    authProvider: string | null;
    storeBackend: string | null;
  };
  security: {
    breakGlassActive: boolean;
    globalWritesDisabled: boolean;
    dangerousWritesDisabled: boolean;
    cloudflareWriteScope: string | null;
    vercelWriteScope: string | null;
    credentialStatus: Array<{ id: string; label: string; status: string; detail: string }>;
  };
  links: {
    uiBaseUrl: string | null;
    founder: string | null;
    tactical: string | null;
    operator: string | null;
    strategy: string | null;
  };
  adapters: Array<{ id: string; status: "healthy" | "degraded" | "offline" | "unknown"; detail: string }>;
  approvals: Array<{ id: string; title: string; riskLevel: string | null; createdAt: string | null }>;
  incidents: Array<{ id: string; title: string; severity: string | null; status: string | null; updatedAt: string | null }>;
};

export type LongwallsJarvisApprovalRecord = {
  id: string;
  title: string;
  riskLevel: string | null;
  createdAt: string | null;
};

export type LongwallsJarvisProgramRecord = {
  id: string;
  name?: string | null;
  status?: string | null;
  openTasks?: number | null;
  blockedTasks?: number | null;
  founderPendingTasks?: number | null;
};

export type LongwallsJarvisIncidentRecord = {
  id: string;
  title: string;
  severity: string | null;
  status: string | null;
  updatedAt: string | null;
};

export type LongwallsJarvisOperationRecord = {
  id: string;
  title?: string | null;
  type?: string | null;
  status?: string | null;
  createdAt?: string | null;
  summary?: string | null;
};

export type LongwallsJarvisAdapterRecord = {
  id?: string;
  adapterId?: string;
  status: "healthy" | "degraded" | "offline" | "unknown";
  detail: string;
};

export type LongwallsJarvisReleaseState = {
  generatedAt?: string;
  status?: string;
  summary?: {
    headline?: string | null;
    releaseStatus?: string | null;
    nextMove?: string | null;
  };
  nextActions?: string[];
  catalogWiring?: {
    projectId: string;
    appName: string | null;
    handoffId: string | null;
    latestRunId: string | null;
    status: "frozen" | "assembled" | "unavailable";
    archetypeId: string | null;
    summary: string[];
    counts: {
      required: number;
      recommended: number;
      optional: number;
      rejected: number;
    };
    sensitiveBrickIds: string[];
    entries: Array<{
      brickId: string;
      title: string;
      family: string;
      selection: "required" | "recommended" | "optional" | "rejected";
      source: string;
      rationale: string[];
      sensitive: boolean;
    }>;
    assembly: {
      status: "assembled" | "blocked" | "unavailable";
      deliveryMode: "local-runnable" | "cloud-ready" | "unknown";
      domainFamily: string | null;
      primaryFlowIds: string[];
      qualityGates: string[];
      evidenceArtifacts: string[];
      customDeltaSummary: string | null;
      customDeltaTrace: {
        summary: string | null;
        domainRuleIds: string[];
        customUiRequirementIds: string[];
        customIntegrationIds: string[];
        blockedUntilIds: string[];
        customSliceIds: string[];
        load: number;
      };
      divergenceProfile: {
        structuralSignature: string;
        moduleCount: number;
        serviceCount: number;
        flowCount: number;
        brickCount: number;
        requiredBrickCount: number;
        customDeltaLoad: number;
        reuseRatio: number;
        depthScore: number;
        divergenceScore: number;
        layoutFamily: string | null;
        workflowFamily: string | null;
        scenario: string | null;
        distinguishingAttributes: string[];
      };
      buildBlockers: {
        blocking: number;
        watch: number;
      };
      securityPack: {
        packId: string;
        title: string;
        controlIds: string[];
        approvalIds: string[];
        rbacTemplateId: string;
        rbacScopeIds: string[];
        tenantIsolationMode: string;
        secretHandlingMode: string;
        ssoProfile: string;
        rollbackMode: string;
        sensitiveBrickIds: string[];
        evidenceBundleId: string;
        evidenceBundleArtifacts: string[];
      } | null;
      qaPack: {
        packId: string;
        title: string;
        primaryFlowIds: string[];
        seededDataNeeds: string[];
      } | null;
      progress: {
        total: number;
        passed: number;
        failed: number;
        skipped: number;
      };
      steps: Array<{
        id: string;
        title: string;
        kind: "foundation" | "module" | "integration" | "qa" | "release";
        riskTier: "low" | "medium" | "high";
        status: "passed" | "failed" | "skipped" | "pending";
        scopeIds: string[];
        dependsOn: string[];
        acceptanceCriteria: string[];
        attempts: number;
        retryBudget: number;
        reason?: string;
      }>;
      summary: string[];
    };
    artifactUrls: {
      handoffPackage: string | null;
      executionSnapshot: string | null;
      deployHandoff: string | null;
      deliveryResult: string | null;
    };
  } | null;
  [key: string]: unknown;
};

export type LongwallsJarvisLearningSnapshot = {
  generatedAt?: string;
  recommendations?: Array<{
    id?: string;
    title?: string;
    detail?: string;
    impact?: string;
  }>;
  [key: string]: unknown;
};

type JsonEnvelope<TPayload, TKey extends string> = {
  ok?: boolean;
  error?: string;
} & Partial<Record<TKey, TPayload>>;

async function readEnvelope<TPayload, TKey extends string>(
  input: RequestInfo | URL,
  key: TKey,
  init?: RequestInit,
): Promise<TPayload> {
  const response = await fetch(input, init);
  const payload = (await response.json().catch(() => null)) as JsonEnvelope<TPayload, TKey> | null;

  if (!response.ok || payload?.ok === false || payload?.[key] === undefined) {
    throw new Error(payload?.error ?? `Failed to load ${key} (status ${response.status})`);
  }

  return payload[key] as TPayload;
}

async function postEnvelope<TPayload, TKey extends string>(
  input: RequestInfo | URL,
  key: TKey,
  body?: unknown,
): Promise<TPayload> {
  return readEnvelope<TPayload, TKey>(input, key, {
    method: "POST",
    headers: body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export async function fetchLongwallsJarvisBridgeOverview(): Promise<LongwallsJarvisBridgeOverview> {
  return readEnvelope("/api/longwalls/jarvis/overview", "overview");
}

export async function fetchLongwallsJarvisApprovals(): Promise<LongwallsJarvisApprovalRecord[]> {
  return readEnvelope("/api/longwalls/jarvis/approvals", "approvals");
}

export async function fetchLongwallsJarvisPrograms(): Promise<LongwallsJarvisProgramRecord[]> {
  return readEnvelope("/api/longwalls/jarvis/programs", "programs");
}

export async function fetchLongwallsJarvisIncidents(status: "active" | "open" | "acknowledged" | "resolved" = "active"): Promise<LongwallsJarvisIncidentRecord[]> {
  return readEnvelope(`/api/longwalls/jarvis/incidents?status=${encodeURIComponent(status)}`, "incidents");
}

export async function fetchLongwallsJarvisOperations(): Promise<LongwallsJarvisOperationRecord[]> {
  return readEnvelope("/api/longwalls/jarvis/operations", "events");
}

export async function fetchLongwallsJarvisLearning(): Promise<LongwallsJarvisLearningSnapshot> {
  return readEnvelope("/api/longwalls/jarvis/learning", "learning");
}

export async function fetchLongwallsJarvisWeeklyBoard(): Promise<Record<string, unknown>> {
  return readEnvelope("/api/longwalls/jarvis/weekly-board", "weeklyBoard");
}

export async function fetchLongwallsJarvisReleaseState(projectId?: string): Promise<LongwallsJarvisReleaseState> {
  const suffix = projectId?.trim() ? `?projectId=${encodeURIComponent(projectId.trim())}` : "";
  return readEnvelope(`/api/longwalls/jarvis/release-state${suffix}`, "releaseState");
}

export async function fetchLongwallsJarvisAdapterHealth(): Promise<LongwallsJarvisAdapterRecord[]> {
  return readEnvelope("/api/longwalls/jarvis/adapter-health", "adapters");
}

export async function approveLongwallsJarvisApproval(approvalId: string, body?: { decisionBy?: string; comment?: string | null }) {
  return postEnvelope(`/api/longwalls/jarvis/approvals/${encodeURIComponent(approvalId)}/approve`, "execution", body);
}

export async function rejectLongwallsJarvisApproval(approvalId: string, body?: { decisionBy?: string; comment?: string | null }) {
  return postEnvelope(`/api/longwalls/jarvis/approvals/${encodeURIComponent(approvalId)}/reject`, "rejection", body);
}

export async function acknowledgeLongwallsJarvisIncident(incidentId: string, body?: { resolvedBy?: string; note?: string | null }) {
  return postEnvelope(`/api/longwalls/jarvis/incidents/${encodeURIComponent(incidentId)}/acknowledge`, "payload", body);
}

export async function resolveLongwallsJarvisIncident(incidentId: string, body?: { resolvedBy?: string; note?: string | null }) {
  return postEnvelope(`/api/longwalls/jarvis/incidents/${encodeURIComponent(incidentId)}/resolve`, "payload", body);
}

export async function replayLongwallsJarvisOperation(
  operationId: string,
  body?: {
    requestedBy?: {
      source?: "agent" | "system" | "founder" | "tactical" | "api";
      roleId?: string | null;
      taskId?: string | null;
      projectId?: string | null;
      domainId?: string | null;
    };
    dryRun?: boolean;
  },
) {
  return postEnvelope(`/api/longwalls/jarvis/operations/${encodeURIComponent(operationId)}/replay`, "replay", body);
}

export async function executeLongwallsJarvisOvernight(
  body?: {
    requestedBy?: {
      source?: "agent" | "system" | "founder" | "tactical" | "api";
      roleId?: string | null;
      taskId?: string | null;
      projectId?: string | null;
      domainId?: string | null;
    };
  },
) {
  return postEnvelope("/api/longwalls/jarvis/overnight/execute", "result", body);
}
